import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import BlogPosts from "../components/BlogPosts";
import InstallPrompt from "../components/InstallPrompt";
import { trackEvent } from "../lib/analytics";
import { PASSWORD_RULES, passwordScore } from "../lib/passwordRules";

/** ---------------- RightPane: separat & stabil ---------------- */
type RightPaneProps = {
  isRegisterMode: boolean;
  showForgotPassword: boolean;
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  showPassword: boolean;
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>;
  isResetting: boolean;

  // Aktionen
  handleSubmitLogin: (e: React.FormEvent) => Promise<void>;
  handleSubmitRegister: (e: React.FormEvent) => Promise<void>;
  handlePasswordReset: (e: React.FormEvent) => Promise<void>;

  // UI-Mode toggles / Meldungen
  setIsRegisterMode: (v: boolean) => void;
  setShowForgotPassword: (v: boolean) => void;
  setError: (v: string) => void;
  setMessage: (v: string) => void;

  error: string;
  message: string;
};

function RightPane(props: RightPaneProps) {
  const {
    isRegisterMode,
    showForgotPassword,
    email,
    setEmail,
    password,
    setPassword,
    isResetting,
    handleSubmitLogin,
    handleSubmitRegister,
    handlePasswordReset,
    setIsRegisterMode,
    setShowForgotPassword,
    showPassword,
    setShowPassword,
    setError,
    setMessage,
    error,
    message,
  } = props;

  // Passwort-Checkliste (nur im Register-UI)
  const checklist =
    isRegisterMode && !showForgotPassword ? (
      <div className="mt-2 text-sm" aria-live="polite">
        <ul className="space-y-1">
          {PASSWORD_RULES.map((rule) => {
            const ok = rule.ok(password);
            return (
              <li key={rule.id} className="flex items-center">
                <span aria-hidden className={`mr-2 ${ok ? "text-emerald-400" : "text-slate-500"}`}>
                  {ok ? "✅" : "⬜"}
                </span>
                <span className={ok ? "text-emerald-400" : "text-slate-400"}>{rule.label}</span>
              </li>
            );
          })}
        </ul>
        <div className="mt-2 h-1 w-full bg-[#2a3347] rounded">
          <div
            className="h-1 rounded transition-all"
            style={{
              width: `${(passwordScore(password) / PASSWORD_RULES.length) * 100}%`,
              backgroundColor:
                passwordScore(password) < 3 ? "#ef4444" :
                passwordScore(password) < 5 ? "#f59e0b" : "#22c55e",
            }}
            aria-hidden
          />
        </div>
      </div>
    ) : null;

  if (showForgotPassword) {
    // ---------- Passwort zurücksetzen ----------
    return (
      <>
        <h1 className="text-2xl font-bold text-center mb-4 text-slate-100">Passwort zurücksetzen</h1>
        <form onSubmit={handleSubmitResetWrapper} className="space-y-4">
          <input
            type="email"
            placeholder="Deine E-Mail-Adresse"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
            required
            autoFocus
          />
          <button
            type="submit"
            disabled={isResetting}
            className={`w-full flex items-center justify-center ${
              isResetting ? "bg-[#2a3347] text-slate-500" : "bg-sky-500 hover:bg-sky-600"
            } text-white py-2 rounded transition-colors`}
          >
            {isResetting ? (
              <div className="flex items-center space-x-2">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4l-3 3 3 3h-4z"
                  />
                </svg>
                <span>Sende...</span>
              </div>
            ) : (
              "Passwort-Reset Link senden"
            )}
          </button>
        </form>

        <div className="text-center mt-4">
          <button
            type="button"
            onClick={() => setShowForgotPassword(false)}
            className="text-slate-400 hover:text-slate-300 text-sm transition-colors"
          >
            Zurück zum Login
          </button>
        </div>
        {error && <p className="text-red-400 mt-4">{error}</p>}
        {message && <p className="text-emerald-400 mt-4">{message}</p>}
      </>
    );
  }

  if (isRegisterMode) {
    // ---------- Registrierung ----------
    return (
      <>

        <h2 className="text-xl font-bold text-center mb-4 text-slate-100">
          Registriere Dich und werde Teil der Svevana Community 👌
        </h2>

        <form onSubmit={handleSubmitRegister} className="space-y-3">
          <input
            type="email"
            autoComplete="off"
            placeholder="E-Mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
            required
            autoFocus
          />
         <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Passwort"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded pr-10 outline-none focus:border-sky-400 placeholder:text-slate-600"
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword((prev: boolean) => !prev)}
          className="absolute inset-y-0 right-2 flex items-center text-slate-500 hover:text-slate-300 text-sm transition-colors"
        >
          {showPassword ? "🙈" : "👁️"}
        </button>
      </div>

          {checklist}

          <button
            type="submit"
            disabled={!PASSWORD_RULES.every((r) => r.ok(password))}
            className="bg-sky-500 text-white px-4 py-2 rounded w-full hover:bg-sky-600 disabled:opacity-50 transition-colors"
          >
            Registrierung abschließen
          </button>
        </form>

        <div className="text-center mt-4">
          <button
            type="button"
            className="text-slate-400 hover:text-slate-300 text-sm transition-colors"
            onClick={() => {
              setIsRegisterMode(false);
              setError("");
              setMessage("");
            }}
          >
            Zurück zum Login
          </button>
        </div>

        {error && <p className="text-red-400 mt-4">{error}</p>}
        {message && <p className="text-emerald-400 mt-4">{message}</p>}
      </>
    );
  }

  // ---------- Login ----------
  return (
    <>
      <h1 className="text-2xl font-bold text-center mb-4 text-slate-100">Login</h1>
      <form onSubmit={handleSubmitLogin} className="space-y-4">
        <input
          type="email"
          placeholder="E-Mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
          required
          autoFocus
        />
        <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Passwort"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded pr-10 outline-none focus:border-sky-400 placeholder:text-slate-600"
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword((prev: boolean) => !prev)}
          className="absolute inset-y-0 right-2 flex items-center text-slate-500 hover:text-slate-300 text-sm transition-colors"
        >
          {showPassword ? "🙈" : "👁️"}
        </button>
      </div>

        <button type="submit" className="w-full bg-sky-500 text-white py-2 rounded hover:bg-sky-600 transition-colors">
          Einloggen
        </button>
      </form>

      <div className="text-center mt-2">
        <button
          type="button"
          className="text-sky-400 hover:underline text-sm"
          onClick={() => {
            setIsRegisterMode(true);
            setShowForgotPassword(false);
            setError("");
            setMessage("");
          }}
        >
          Noch kein Konto? Jetzt registrieren
        </button>
      </div>

      <div className="text-center mt-4">
        <button
          type="button"
          className="text-sky-400 hover:underline text-sm"
          onClick={() => {
            setShowForgotPassword(true);
            setIsRegisterMode(false);
            setError("");
            setMessage("");
          }}
        >
          Passwort vergessen?
        </button>
      </div>

      {error && <p className="text-red-400 mt-4">{error}</p>}
      {message && <p className="text-emerald-400 mt-4">{message}</p>}
    </>
  );

  // lokaler Wrapper, damit TS glücklich ist (FormEvent in Props)
  function handleSubmitResetWrapper(e: React.FormEvent) {
    return handlePasswordReset(e);
  }
}

/** ---------------- Hauptkomponente ---------------- */
export default function Landing() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [showLogin, setShowLogin] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // statt "Modal": exklusiver Modus – entweder Login ODER Registrierung
  const [isRegisterMode, setIsRegisterMode] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get("redirect");

  useEffect(() => {
    const timer = setTimeout(() => setShowLogin(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /** ---------------- Passwort-Reset ---------------- */
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsResetting(true);

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      setError("❌ Fehler beim Senden des Reset-Links. Überprüfe die E-Mail-Adresse.");
    } else {
      setMessage("✅ Passwort-Reset-Link wurde an deine E-Mail gesendet.");
      setShowForgotPassword(false);
    }

    setIsResetting(false);
  };

  /** ---------------- Login ---------------- */
  const handleSubmitLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    const { data: loginData, error: loginError } =
      await supabase.auth.signInWithPassword({ email, password });

    if (loginData?.user && !loginError) {
      // Profil ggf. anlegen
      const { data: existingProfile, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", loginData.user.id)
        .maybeSingle();

      if (!existingProfile && !profileError) {
        await supabase.from("profiles").insert([
          { id: loginData.user.id, email: loginData.user.email, role: "user" },
        ]);
      }

      // ✅ GA4: Login-Event senden (erst NACH Erfolg)
      trackEvent("login_success", {
        method: "password",
        user_id: loginData.user.id,
        debug_mode: true, // hilft beim Test in GA DebugView
      });

      navigate(redirectTo || "/dashboard");
      return;
    }

    const msg = loginError?.message?.toLowerCase() ?? "";
    if (msg.includes("email") && msg.includes("confirm")) {
      setError("❌ Deine E-Mail ist noch nicht bestätigt. Bitte Postfach prüfen.");
    } else if (msg.includes("invalid login credentials")) {
      setError("❌ Login fehlgeschlagen. E-Mail oder Passwort falsch?");
    } else {
      setError(`❌ Login fehlgeschlagen: ${loginError?.message ?? "Unbekannter Fehler"}`);
    }
  };


  /** ---------------- Registrierung ---------------- */
  const handleSubmitRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    // lokale Regeln prüfen
    const allOk = PASSWORD_RULES.every((r) => r.ok(password));
    if (!allOk) {
      setError("❌ Bitte erfülle zuerst alle Passwort-Regeln.");
      return;
    }

    const { data, error } = await supabase.auth.signUp({ email, password });

    if (error) {
      const msg = error.message?.toLowerCase() ?? "";
      if (error.status === 429) {
        setError("⏳ Zu viele Anfragen – bitte kurz warten und erneut versuchen.");
      } else if (msg.includes("already registered") || msg.includes("already exists")) {
        setError("❌ Diese E-Mail ist bereits registriert.");
      } else if (msg.includes("password")) {
        setError(`❌ Passwort abgelehnt: ${error.message}`);
      } else {
        setError(`❌ Registrierung fehlgeschlagen: ${error.message}`);
      }
      return;
    }

    // ✅ Analytics: Registrierung erfolgreich
    trackEvent("registration_success", {
      method: "password",
      user_id: data.user?.id ?? "pending_email_confirmation",
      debug_mode: true, // für DebugView
    });

    // Bei aktivierter E-Mail-Bestätigung ist data.user oft null – das ist okay.
    setMessage("✅ Bitte bestätige deine E-Mail. Danach kannst du dich einloggen.");
    setIsRegisterMode(false);
    setShowForgotPassword(false);
  };


  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="min-h-screen flex flex-col md:flex-row relative bg-[#0d1117]">
      <InstallPrompt />

      {/* Blogbereich (links) */}
    <div className="flex-1 flex flex-col p-6 pb-64 md:pb-6">
      <BlogPosts readonly />

      {/* ⬇️ Nicht-sticky Footer unter dem Blog */}
      <SiteFooter />
    </div>




      {/* Rechts: exklusives Pane (Login ODER Registrierung ODER Reset) */}
      <div
        className={`w-full md:w-96 bg-[#161b27] border-t border-[#2a3347] md:border-t-0 md:border-l p-6 shadow-lg md:static md:h-auto fixed bottom-0 left-0 right-0 transition-all duration-500 transform ${
          showLogin ? "translate-y-0" : "translate-y-10"
        }`}
      >
        <RightPane
          isRegisterMode={isRegisterMode}
          showForgotPassword={showForgotPassword}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          isResetting={isResetting}
          handleSubmitLogin={handleSubmitLogin}
          handleSubmitRegister={handleSubmitRegister}
          handlePasswordReset={handlePasswordReset}
          setIsRegisterMode={setIsRegisterMode}
          setShowForgotPassword={setShowForgotPassword}
          setError={setError}
          setMessage={setMessage}
          error={error}
          message={message}
        />
      </div>

      {/* Scroll-to-Top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-28 right-4 md:bottom-8 md:right-8 bg-sky-500 hover:bg-sky-600 text-white p-3 rounded-full shadow-lg transition-colors z-50"
          aria-label="Nach oben scrollen"
        >
          ⬆️
        </button>
      )}
    </div>
  );
}

function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="mt-12 text-xs text-slate-500">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div>
          <Link to="/rechtliches" className="hover:text-slate-300 transition-colors">Rechtliches & Datenschutz</Link>
        </div>
      </div>
      <div className="mt-6 border-t border-[#2a3347] pt-4 flex flex-wrap items-center justify-between gap-2">
        <p>© {year} Svevana</p>
        <p>Made with ❤️ in Switzerland</p>
      </div>
    </footer>
  );
}
